import React, { useState, useEffect, useRef, Fragment } from "react";
import PropTypes from "prop-types";
import Checkbox from "./Checkbox";
import Badge from "./Badge";
import { createPortal } from "react-dom";
import Search from "./Search";
const InputDropdown = ({
  label,
  options = [],
  value,
  onChange,
  icon = null,
  multiple = false,
  searchable = false,
  creatable = false,
  placeholder = "Select",
  className = "",
  name = "input-dropdown",
  hintText = "",
  destructive = false,
  disabled = false,
  onCreateOption,
  placement = "bottom-start",
  childrenPlacement = "right",
  childrenSearch = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef();
  // ✅ Unique id so the outside-click handler can ignore clicks inside portalled menus
  const dropdownId = useRef(`dd-${Math.random().toString(36).slice(2)}`);

  // useEffect(() => {
  //   const handler = (e) => {
  //     // Ignore clicks inside the trigger wrapper
  //     if (ref.current && ref.current.contains(e.target)) return;
  //     // ✅ Ignore clicks inside any portalled menu that belongs to this dropdown
  //     //    (nested menus are rendered in document.body via createPortal, so they
  //     //     are outside `ref` — without this check they always close the dropdown)
  //     if (e.target.closest(`[data-dropdown-id="${dropdownId.current}"]`))
  //       return;
  //     setOpen(false);
  //     setQuery("");
  //   };
  //   document.addEventListener("mousedown", handler);
  //   return () => document.removeEventListener("mousedown", handler);
  // }, []);

  const filteredOptions = options.filter((opt) =>
    opt.label?.toLowerCase().includes(query.toLowerCase()),
  );

  const safeValue = multiple ? value ?? [] : value;

  const selectOption = (val) => {
    if (disabled) return;
    if (multiple) {
      let updated = [];
      if (safeValue.includes(val)) {
        updated = safeValue.filter((v) => v !== val);
      } else {
        updated = [...safeValue, val];
      }
      onChange(updated);
    } else {
      onChange(val);
      setOpen(false);
    }
    setQuery("");
  };

  const handleCreate = () => {
    if (!creatable || !query.trim()) return;

    const newOpt = {
      label: query,
      value: query,
    };

    options.push(newOpt);

    if (multiple) onChange([...value, newOpt.value]);
    else onChange(newOpt.value);
    onCreateOption && onCreateOption(newOpt);

    setQuery("");
    setOpen(false);
  };

  // ✅ Deep search through children first to avoid parent/child value collisions
  // e.g. parent "General" value:1 AND child "HTML" value:1 — child should win
  const findOptionDeep = (opts, val) => {
    for (const opt of opts) {
      if (opt.children?.length) {
        const found = findOptionDeep(opt.children, val);
        if (found) return found;
      }
      if (opt.value === val) return opt;
    }
    return undefined;
  };

  const displayValue = multiple
    ? safeValue.map((v) => findOptionDeep(options, v)).filter(Boolean)
    : findOptionDeep(options, value);

  return (
    <div
      className={`input-dropdown-wrapper${destructive ? " destructive" : ""}${
        disabled ? " disabled" : ""
      } ${className}`}
      ref={ref}
    >
      {label && <label className="input-dropdown-label">{label}</label>}

      <div
        className={`input-dropdown-control ${
          open ? "input-dropdown-open" : ""
        }`}
        onClick={() => !disabled && setOpen(!open)}
      >
        <div className="input-dropdown-left" style={{ display: "flex" }}>
          {icon && icon}

          {/* Multi Select Tags */}
          {multiple ? (
            <div className="input-dropdown-tags">
              {displayValue.length > 0
                ? displayValue.map((item) => (
                    <Badge
                      key={item.value}
                      size="sm"
                      color="grey"
                      label={<Fragment key={item.value}>{item.label}</Fragment>}
                      icon={{
                        icon: (
                          <svg
                            onClick={(e) => {
                              e.stopPropagation();
                              selectOption(item.value);
                            }}
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9 3L3 9M3 3L9 9"
                              stroke="#344054"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            ></path>
                          </svg>
                        ),
                        position: "end",
                      }}
                    />
                  ))
                : !creatable &&
                  !searchable && (
                    <span className="input-dropdown-placeholder">
                      {placeholder}
                    </span>
                  )}
              {creatable || searchable ? (
                <input
                  className="input-dropdown-search"
                  placeholder={placeholder ?? "Search..."}
                  autoComplete="off"
                  name={name}
                  value={open ? query : ""}
                  onChange={(e) => {
                    e.stopPropagation();
                    !open&&setOpen(true)
                    setQuery(e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                />
              ) : null}
            </div>
          ) : searchable || creatable ? (
            <input
              className="input-dropdown-search"
              placeholder={placeholder ?? "Search..."}
              autoComplete="off"
              name={name}
              value={open ? query : displayValue?.label ?? ""}
              onChange={(e) => {
                e.stopPropagation();
                !open&&setOpen(true)
                setQuery(e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
            />
          ) : (
            <span
              className={`${
                displayValue
                  ? "input-dropdown-value"
                  : "input-dropdown-placeholder"
              }`}

              title={displayValue?.label || ""}
            >
              {displayValue ? displayValue.label : placeholder}
            </span>
          )}
        </div>

        <span className="input-dropdown-icon">
          {open ? (
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 12.5L10 7.5L5 12.5"
                stroke="#667085"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="#667085"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>
      </div>

      {hintText && <p className="input-dropdown-hint-text">{hintText}</p>}

      {open && (
        <DropdownMenu
          options={filteredOptions}
          onSelectOption={selectOption}
          multiple={multiple}
          anchorRef={ref}
          open={open}
          value={safeValue}
          query={query}
          onCreate={handleCreate}
          creatable={creatable}
          placement={placement}
          childrenPlacement={childrenPlacement}
          childrenSearch={childrenSearch}
          onRootSelect={selectOption}
          // ✅ Tag portalled menus so outside-click handler can exclude them
          dropdownId={dropdownId.current}
          onClose={() => { setOpen(false); setQuery(""); }}
        />
      )}
    </div>
  );
};

export const DropdownMenu = ({
  open,
  onClose=null,
  options,
  onSelectOption = null,
  multiple = false,
  anchorRef,
  value,
  query,
  creatable,
  onCreate = null,
  className = "",
  style = {},
  placement = "bottom-start",
  offset = 0,
  childrenPlacement = "right",
  childrenSearch = false,
  onMouseEnter,
  onMouseLeave,
  onRootSelect = null,
  // ✅ Identifier stamped on the portalled div so outside-click can exclude it
  dropdownId = null,
  searchable = false,
  childQuery = "",
  setChildQuery = null,
  ...props
}) => {
  const [menuStyle, setMenuStyle] = useState({});
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open || !onClose) return;
    const handler = (e) => {
      if (anchorRef?.current?.contains(e.target)) return;
      if (dropdownId && e.target.closest(`[data-dropdown-id="${dropdownId}"]`)) return;
      onClose();
    };
    document.addEventListener("mousedown", handler,true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [open, onClose, anchorRef, dropdownId]);

  const updatePosition = () => {
    if (!anchorRef?.current) return;

    const rect = anchorRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();


    const positions = {
      "top-start": {
        top: rect.top + window.scrollY - menuRect.height - offset,
        left: rect.left + window.scrollX,
        width: style.width || rect.width,
      },
      "top-end": {
        top: rect.top + window.scrollY - menuRect.height - offset,
        left: rect.right + window.scrollX,
        width: style.width || rect.width,
      },
      "bottom-start": {
        top: rect.bottom + window.scrollY + offset,
        left: rect.left + window.scrollX,
        width: style.width || rect.width,
      },
      "bottom-end": {
        top: rect.bottom + window.scrollY + offset,
        left: rect.right + window.scrollX,
        width: style.width || rect.width,
      },
      right: {
        top: rect.top + window.scrollY,
        left: rect.right + window.scrollX + offset,
        width: style.width || rect.width,
      },
      left: {
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX - rect.width - offset - 16,
        width: style.width || rect.width,
      },
    };

    setMenuStyle({
      position: "absolute",
      zIndex: 9999,
      ...positions[placement],
    });
  };

  useEffect(() => {
    if (open) updatePosition();
  }, [open]);

  useEffect(() => {
    window.addEventListener("resize", updatePosition, true);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition, true);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, []);

  if (!open) return null;

  return createPortal(
    <div
      className={`input-dropdown-menu ${className}`}
      style={{ ...style, ...menuStyle }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // ✅ This attribute lets the outside-click handler identify portalled menus
      data-dropdown-id={dropdownId}
      ref={menuRef}
      {...props}
    >
      <div className="input-dropdown-options">
        {searchable && (
          <div className="input-dropdown-search-wrapper">
            <Search
              placeholder="Search Topic"
              value={childQuery}
              onChange={(e) => setChildQuery(e.target.value)}
              width="100%"
              height="36px"
            />
          </div>
        )}
        {!options.length && (
          <p className="input-dropdown-no-option">No Option</p>
        )}
        {options.map((opt) => (
          <InputDropdownOption
            key={opt.value}
            value={opt.value}
            label={opt.label}
            selectedValue={value}
            multiple={multiple}
            onClick={(val) => onSelectOption(val)}
            icon={opt.icon}
            disabled={!!opt.disabled}
            selected={
              opt.children?.length
                ? opt.children.some((op) => op.value === value)
                : multiple
                ? value?.includes(opt.value)
                : value === opt.value
            }
            children={opt.children}
            childrenPlacement={childrenPlacement}
            childrenSearch={childrenSearch}
            onRootSelect={onRootSelect ?? onSelectOption}
            // ✅ Thread dropdownId down so nested menus are also tagged
            dropdownId={dropdownId}
          />
        ))}

        {creatable &&
          query.trim() !== "" &&
          !options.some(
            (opt) => opt.label.toLowerCase() === query.toLowerCase(),
          ) && (
            <div className="input-dropdown-create" onClick={onCreate}>
              Create "{query}"
            </div>
          )}
      </div>
    </div>,
    document.body,
  );
};

export const InputDropdownOption = ({
  value,
  label,
  icon,
  disabled,
  onClick,
  selected,
  selectedValue,
  className = "",
  multiple = false,
  children = null,
  childrenPlacement,
  childrenSearch = false,
  onRootSelect = null,
  // ✅ Passed through to nested menus so they carry the same dropdownId
  dropdownId = null,
}) => {
  const [childQuery, setChildQuery] = useState("");
  const [nestedOpen, setNestedOpen] = useState(false);
  const filteredChildren =
    children?.filter((child) =>
        child.label
            .toLowerCase()
            .includes(childQuery.toLowerCase())
    ) || [];
  const closeTimer = useRef(null);
  const ref = useRef(null);

  const handleMouseEnter = () => {
    if (children?.length > 0) {
      clearTimeout(closeTimer.current);
      setNestedOpen(true);
    }
  };

  const handleMouseLeave = () => {
    if (children?.length > 0) {
      closeTimer.current = setTimeout(() => setNestedOpen(false), 100);
    }
  };

  const handleNestedMouseEnter = () => {
    clearTimeout(closeTimer.current);
  };

  const handleNestedMouseLeave = () => {
      closeTimer.current = setTimeout(() => {
          setNestedOpen(false);
          setChildQuery("");
      },100);
  }

  const onClickOption = (event) => {
    event.stopPropagation();
    if (disabled) return;
    if (children?.length > 0) return;
    onClick && onClick(value);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  return (
    <>
      <div
        className={`input-dropdown-option${
          selected ? " input-dropdown-option-selected" : ""
        }${disabled ? " input-dropdown-option-disabled" : ""}${
          children?.length > 0 ? " input-dropdown-option-has-children" : ""
        } ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={onClickOption}
        ref={ref}
      >
        <div className="input-dropdown-option-left">
          {multiple && (
            <Checkbox
              disabled={!!disabled}
              checked={selected}
              onChange={() => (disabled ? null : onClick(value))}
            />
          )}
          {icon && icon}
          {label}
        </div>

        {selected && !multiple ? (
          <svg
            className="input-dropdown-option-selected-icon"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M16.6668 5L7.50016 14.1667L3.3335 10"
              stroke="#F55533"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : null}
      </div>

      {nestedOpen && children?.length > 0 && (
        <DropdownMenu
          options={filteredChildren}
          searchable={childrenSearch}
          childQuery={childQuery}
          setChildQuery={setChildQuery}
          onSelectOption={onRootSelect ?? onClick}
          onRootSelect={onRootSelect ?? onClick}
          multiple={multiple}
          value={selectedValue}
          className="input-dropdown-menu-nested"
          anchorRef={ref}
          open={nestedOpen}
          placement={childrenPlacement}
          onMouseEnter={handleNestedMouseEnter}
          onMouseLeave={handleNestedMouseLeave}
          // ✅ Same dropdownId so outside-click handler ignores this nested portal too
          dropdownId={dropdownId}
        />
      )}
    </>
  );
};

export default InputDropdown;

InputDropdown.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  options: PropTypes.array,
  name: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  hintText: PropTypes.string,
  destructive: PropTypes.bool,
  icon: PropTypes.node,
  multiple: PropTypes.bool,
  searchable: PropTypes.bool,
  creatable: PropTypes.bool,
  onCreateOption: PropTypes.func,
  placement: PropTypes.oneOf(["bottom-start", "bottom-end", "right", "left"]),
  childrenPlacement: PropTypes.oneOf([
    "bottom-start",
    "bottom-end",
    "right",
    "left",
  ]),
  childrenSearch: PropTypes.bool,
};

InputDropdown.defaultProps = {
  options: [],
  disabled: false,
  placeholder: "Select",
  destructive: false,
  multiple: false,
  searchable: false,
  childrenSearch: false,
  creatable: false,
};
